import { QueryClient, VueQueryPlugin } from '@tanstack/vue-query'
import { experimental_createQueryPersister } from '@tanstack/query-persist-client-core'
import { get, set, del, entries } from 'idb-keyval'
import type { App } from 'vue'

export const persister = experimental_createQueryPersister({
	storage: {
		getItem: (key: string) => get(key),
		setItem: (key: string, value: string) => set(key, value),
		removeItem: (key: string) => del(key),
		entries: () => entries<string>(),
	},
	//maxAge: 1000 * 60 * 60 * 12, // 12 hours
})

// AAR-1483: Diese defaultOptions galten bis dato NIE — der Default-Export war
// das nackte VueQueryPlugin, das ohne Optionen installiert wurde und sich
// intern einen eigenen QueryClient mit Stock-Defaults anlegte (staleTime 0,
// gcTime 5 Min., Fokus-/Mount-/Reconnect-Refetch an, kein Persister). Der
// Wrapper-Plugin-Export unten verdrahtet erstmals DIESEN Client in die App.
//
// Bewusste Policy gegenüber den Stock-Defaults:
// - staleTime 5 Min. als Drossel: Fokus-/Mount-/Reconnect-Refetch greifen nur
//   für tatsächlich stale Daten (vorher: staleTime 0 = Refetch bei jedem Anlass).
// - gcTime 30 Min. statt 5 Min.
// - refetchOnWindowFocus/Mount/Reconnect bleiben an (= Stock-Verhalten, das die
//   App global effektiv schon immer hatte). Ausnahme: Tabellen (useTable) hatten
//   bisher ein refetchOnWindowFocus: false-Override, das in diesem PR entfernt
//   wird — dort ist der Fokus-Refetch also neu (Mount/Reconnect waren auch in
//   useTable schon an).
// - KEIN globaler Persister: der experimentelle IndexedDB-Persister war durch
//   die fehlende Verdrahtung nie aktiv und wird hier bewusst NICHT als
//   Nebenwirkung aktiviert (separates Follow-up). Der Export bleibt für die
//   per-Query-Nutzung in useTable (usePersister/offline) bestehen.
export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: (import.meta.env.VITE_QUERY_STALE_TIME || 5) * 60 * 1000,
			gcTime: (import.meta.env.VITE_QUERY_GC_TIME || 30) * 60 * 1000,
			refetchOnWindowFocus: true,
			refetchOnMount: true,
			refetchOnReconnect: true,
		},
	},
})

// Wrapper statt nacktem VueQueryPlugin: installiert das Plugin MIT dem oben
// konfigurierten Client. `app.use(VueQueryPlugin)` ohne Optionen würde einen
// zweiten, unkonfigurierten Client erzeugen (der Bug, der die gesamte
// Konfiguration hier wirkungslos machte).
const queryClientPlugin = {
	install(app: App) {
		app.use(VueQueryPlugin, { queryClient })
	},
}

export default queryClientPlugin
